import { CircularProgress, TableCell, Tooltip, Typography } from '@mui/material';
import { COST_PREVIEW_STATUS } from '../hooks/useCostPreview';
import { colors, formatCurrency } from '../constants';

const DASH = '\u2014';
// Rows are top-aligned so input helper texts can grow downwards; the padding
// lines plain values up with the text inside the neighbouring inputs.
const VALUE_SX = { whiteSpace: 'nowrap', pt: 1.75 };
const MUTED_SX = { ...VALUE_SX, color: colors.gray600 };
const CHANGE_SX = { display: 'block', fontSize: 11, lineHeight: 1.2 };

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const changeLabel = (current, next) => {
  if (current == null || next == null || current === 0) return null;
  const pct = ((next - current) / current) * 100;
  const sign = pct > 0 ? '+' : '';
  return { text: `(${sign}${pct.toFixed(2)}%)`, direction: Math.sign(pct) };
};

const CHANGE_COLOR = {
  1: colors.error,
  0: colors.gray500,
  [-1]: colors.success,
};

/**
 * Current Quantity and Total Quantity. A new (unlinked) item has no stock yet,
 * so its total is just the quantity being added.
 */
export function StockCells({ selectedItem, quantity }) {
  const qty = toNumber(quantity) ?? 0;
  const currentStock = selectedItem ? toNumber(selectedItem.stock) : null;
  const total = selectedItem ? (currentStock ?? 0) + qty : qty;
  return (
    <>
      <TableCell align="right" sx={MUTED_SX}>
        {selectedItem ? currentStock ?? DASH : DASH}
      </TableCell>
      <TableCell align="right" sx={MUTED_SX}>
        {total}
      </TableCell>
    </>
  );
}

/**
 * Line total, Current Cost and New Cost. New Cost for a linked item comes from
 * the backend preview; a new item simply takes its unit cost.
 */
export function CostCells({
  selectedItem,
  quantity,
  unitCost,
  previewStatus,
  previewNewCost,
  previewError,
}) {
  const qty = toNumber(quantity) ?? 0;
  const cost = toNumber(unitCost);
  const lineTotal = qty * (cost ?? 0);
  const currentCost = selectedItem ? toNumber(selectedItem.unitPrice) : null;

  let newCost;
  if (!selectedItem) {
    newCost = cost == null ? (
      DASH
    ) : (
      <>
        {formatCurrency(cost)}
        <Typography component="span" sx={{ ...CHANGE_SX, color: colors.gray500 }}>
          (new)
        </Typography>
      </>
    );
  } else if (previewStatus === COST_PREVIEW_STATUS.LOADING) {
    newCost = <CircularProgress size={14} aria-label="Calculating new cost" />;
  } else if (previewStatus === COST_PREVIEW_STATUS.ERROR) {
    newCost = (
      <Tooltip title={`Could not calculate the new cost. ${previewError || ''}`.trim()}>
        <Typography component="span" sx={{ color: colors.error, cursor: 'help' }}>
          {DASH}
        </Typography>
      </Tooltip>
    );
  } else if (previewStatus === COST_PREVIEW_STATUS.READY && previewNewCost != null) {
    const change = changeLabel(currentCost, previewNewCost);
    newCost = (
      <>
        {formatCurrency(previewNewCost)}
        {change && (
          <Typography
            component="span"
            sx={{ ...CHANGE_SX, color: CHANGE_COLOR[change.direction] }}
          >
            {change.text}
          </Typography>
        )}
      </>
    );
  } else {
    newCost = DASH;
  }

  return (
    <>
      <TableCell align="right" sx={VALUE_SX}>
        {formatCurrency(lineTotal)}
      </TableCell>
      <TableCell align="right" sx={MUTED_SX}>
        {currentCost != null ? formatCurrency(currentCost) : DASH}
      </TableCell>
      <TableCell align="right" sx={VALUE_SX}>
        {newCost}
      </TableCell>
    </>
  );
}
